export interface PersistedQueuedTask {
  taskId: string;
  accountId: string;
  sessionId: string;
  threadId?: string;
  prompt?: string;
  queuedAt: number;
}

interface MinimalChatMessage {
  id?: string;
  isUser?: boolean;
  interrupted?: boolean;
  status?: string;
  sourceTag?: string;
  text?: string;
  html?: string;
  serverTaskId?: string;
  taskAnchorId?: string;
  regeneratePrompt?: string;
  errorInfo?: unknown;
}

// Pending/in-flight assistant placeholder tags. A trailing bubble carrying one
// of these (or already marked interrupted) is a stale placeholder that should be
// REPLACED by the recovered answer, not left sitting above it.
const PENDING_PLACEHOLDER_TAGS = new Set<string>([
  'pending',
  'immediate_thinking_pending',
  'stage1_assessment_pending',
  'attachment_read_pending',
  'pre_orchestrator_attachment_read_pending',
  'prest0n_session_streaming',
]);

function isStalePlaceholder(m: MinimalChatMessage): boolean {
  if (m.interrupted || m.status === 'interrupted') return true;
  // Durability audit rank #19 (2026-07-13): cover onToolStart's rotated
  // 'prest0n_tool_start_<toolId>' pending widgets (see interruptedTurn.ts).
  if (typeof m.sourceTag === 'string' && m.sourceTag.startsWith('prest0n_tool_start_')) return true;
  if (m.status === 'complete') return false;
  return !!m.sourceTag && PENDING_PLACEHOLDER_TAGS.has(m.sourceTag);
}

/**
 * The RETRY-STABLE thread anchor of a polled task (2026-08-27, the duplicated
 * "I hit an error… (ref: pn-mtaupwuc-ova20)" card): every SERVER writer keys
 * a turn's terminal card on `prest0n_task_<threadAnchorTaskId>` — the
 * identity of the turn the human asked for, carried across every watchdog /
 * empty-delivery rescue hop in dispatchMetadata. A client writer that keys on
 * the raw hop taskId instead lands its copy BESIDE the server's under a
 * different id — two identical cards, same ref, forever. Same derivation as
 * the server's resolveThreadAnchorTaskId: stamped anchor → own id.
 */
export function resolveClientTaskAnchorId(task: {
  taskId: string;
  dispatchMetadata?: { threadAnchorTaskId?: unknown };
}): string {
  const stamped = task?.dispatchMetadata?.threadAnchorTaskId;
  return (typeof stamped === 'string' && stamped.trim()) ? stamped.trim() : task.taskId;
}

/**
 * Merge a recovered (server-completed) assistant answer into a thread's message
 * list. If the most recent assistant bubble is an interrupted card (the stale
 * "Interrupted by page refresh" placeholder left when the user refreshed mid
 * turn), REPLACE it in place so the recovered answer doesn't stack below a
 * scary leftover card. Otherwise append. Idempotent: if the recovered message
 * id is already present, returns the list unchanged.
 */
export function mergeRecoveredAnswerIntoMessages<T extends MinimalChatMessage>(
  existing: T[],
  recovered: T,
  opts?: {
    turnPrompt?: string;
    taskId?: string;
  },
): T[] {
  if (recovered.id && existing.some((m) => m && m.id === recovered.id)) {
    return existing;
  }

  const taskId = (opts?.taskId || '').trim();
  const turnPrompt = (opts?.turnPrompt || '').trim();

  const normalizeRecovered = (idOverride?: string): T => {
    const next = {
      ...recovered,
      ...(idOverride ? { id: idOverride } : {}),
      interrupted: false,
      status: 'complete',
      regeneratePrompt: undefined,
      errorInfo: undefined,
    } as T;
    return next;
  };

  // Anchor-match convergence (Round K — mirrors the server's
  // mergeRecoveredAnswerIntoMessages in functions/src/prest0nSession/
  // taskQueue.ts): the client persists the SAME turn under its stream id —
  // not the prest0n_task_<id> namespace — with a `serverTaskId` marker, and
  // finalize clears serverTaskId but freezes the durable `taskAnchorId`.
  // When that copy exists, converge on it instead of walking placeholders:
  //   • finalized copy → the live stream already delivered this answer.
  //     Return unchanged: appending would render a duplicate bubble and
  //     replacing could clobber a richer live body.
  //   • pending/interrupted copy → upgrade it in place, keeping its id so
  //     client-side references (dedupe, resume, finalize-by-id) stay valid.
  const recoveredAnchor = typeof recovered.taskAnchorId === 'string' ? recovered.taskAnchorId.trim() : '';
  if (taskId || recoveredAnchor) {
    const idx = existing.findIndex(
      (m) => m && !m.isUser && (
        (typeof m.serverTaskId === 'string' && !!taskId && m.serverTaskId === taskId) ||
        (typeof m.taskAnchorId === 'string' && !!recoveredAnchor && m.taskAnchorId === recoveredAnchor)
      ),
    );
    if (idx >= 0) {
      const current = existing[idx];
      if (!isStalePlaceholder(current)) return existing;
      const next = existing.slice();
      const upgraded = normalizeRecovered(current.id || recovered.id);
      // The anchor is write-once — keep the frozen one if the recovered
      // copy lacks it, so the per-doc/merge key survives the upgrade.
      if (!upgraded.taskAnchorId && current.taskAnchorId) {
        upgraded.taskAnchorId = current.taskAnchorId;
      }
      next[idx] = upgraded;
      return next;
    }
  }

  // Turn-aware replacement: if we can locate the originating user prompt,
  // consolidate that turn to a single assistant completion and drop stale
  // interrupted/pending residue.
  if (turnPrompt) {
    let userIndex = -1;
    for (let i = existing.length - 1; i >= 0; i -= 1) {
      const m = existing[i];
      if (!m?.isUser) continue;
      if ((m.text || '').trim() !== turnPrompt) continue;
      userIndex = i;
      break;
    }

    if (userIndex >= 0) {
      let turnEnd = existing.length;
      for (let i = userIndex + 1; i < existing.length; i += 1) {
        if (existing[i]?.isUser) {
          turnEnd = i;
          break;
        }
      }

      const assistantIndices: number[] = [];
      for (let i = userIndex + 1; i < turnEnd; i += 1) {
        if (!existing[i]?.isUser) assistantIndices.push(i);
      }

      if (assistantIndices.length > 0) {
        const completedIndices = assistantIndices.filter((i) => !isStalePlaceholder(existing[i]));
        const keepIndex = (completedIndices.length > 0
          ? completedIndices[completedIndices.length - 1]
          : assistantIndices[0]);

        const out = existing
          .map((message, index) => {
            if (index === keepIndex) {
              return normalizeRecovered(message?.id || recovered.id);
            }
            if (!assistantIndices.includes(index)) return message;
            return isStalePlaceholder(message) ? null : message;
          })
          .filter((message): message is T => message !== null);

        return out;
      }
    }
  }

  for (let i = existing.length - 1; i >= 0; i -= 1) {
    const m = existing[i];
    if (!m) continue;
    if (m.isUser) continue;
    if (isStalePlaceholder(m)) {
      const next = existing.slice();
      next[i] = normalizeRecovered(m.id || recovered.id);
      return next;
    }
    // A completed assistant bubble before any stale placeholder: nothing to replace.
    break;
  }
  return [...existing, normalizeRecovered(recovered.id)];
}

const STORAGE_PREFIX = 'prest0n_queued_task';

function storageKey(accountId: string, sessionId: string): string {
  return `${STORAGE_PREFIX}:${accountId}:${sessionId}`;
}

export function persistQueuedTask(task: PersistedQueuedTask): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey(task.accountId, task.sessionId), JSON.stringify(task));
}

export function loadQueuedTask(accountId: string, sessionId: string): PersistedQueuedTask | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(storageKey(accountId, sessionId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PersistedQueuedTask;
    if (!parsed || typeof parsed.taskId !== 'string' || parsed.taskId.trim().length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearQueuedTask(accountId: string, sessionId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(storageKey(accountId, sessionId));
}

export async function resumeQueuedTaskOnHydrate(params: {
  accountId: string;
  sessionId: string;
  getTaskStatus: (taskId: string) => Promise<unknown | null>;
}): Promise<PersistedQueuedTask | null> {
  const persisted = loadQueuedTask(params.accountId, params.sessionId);
  if (!persisted) return null;
  // 2026-08-14 duplicate-reply fix: honor the status probe instead of
  // discarding it. The pre-flight queued-task record is written on EVERY
  // lane (including inline SSE turns that never mint a server task doc), so
  // resuming unconditionally armed a recovery poller — with the
  // persist-completion writer enabled — for tasks that never existed, one of
  // the duplicate-answer writers. A SERVED null means no such task: clear
  // the record and stand down. A network-shaped probe failure must THROW
  // from getTaskStatus — that keeps the record for the next hydrate instead
  // of abandoning a genuinely-running task on a flaky connection.
  let status: unknown | null;
  try {
    status = await params.getTaskStatus(persisted.taskId);
  } catch {
    return persisted;
  }
  if (status === null) {
    // INCIDENT-20260820 (Fix 5): parity with the resume path's min-age gate
    // (serverTaskResume.TASK_MISSING_MIN_AGE_MS — equality pinned by test).
    // The pre-flight record is stamped BEFORE the server task doc exists, so
    // a served null on a young record is the enqueue race, not a missing
    // task — clearing it on the first miss abandoned genuinely-running
    // turns that hydrated within the race window.
    const ageMs = Date.now() - (Number(persisted.queuedAt) || 0);
    if (Number(persisted.queuedAt) > 0 && ageMs < QUEUED_TASK_MISSING_MIN_AGE_MS) {
      return persisted;
    }
    clearQueuedTask(params.accountId, params.sessionId);
    return null;
  }
  return persisted;
}

/** Must equal serverTaskResume.TASK_MISSING_MIN_AGE_MS (a services module
 *  cannot import from components without inverting the layering — the
 *  equality is pinned by taskRecovery.test.ts instead). */
export const QUEUED_TASK_MISSING_MIN_AGE_MS = 60_000;
